import React, { useState } from "react";
import { Box, Button, Grid, Typography } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { palette } from "../palette";
import { fadeUp, revealProps, stagger, useReducedMotion } from "../motion";
import { Section } from "./Section";
import { PersonaScene } from "../illustrations/Personas";

// Sample plans, not a curriculum. Each one is a real shape we designed for a
// particular person — shown so visitors can see what a plan looks like, not so
// they pick one off a menu.
const TRACKS = [
  {
    id: "scratch",
    label: "The data analyst",
    from: "Data Analyst",
    to: "Data Scientist",
    subtitle: "Already worked with data daily. Wanted the fundamentals of AI, not a shortcut to LLMs.",
    description:
      "Could write SQL and read a dashboard, but everything underneath the models was a black box. They deliberately chose not to jump straight to prompting and agents — they wanted to understand why a model works before using one. So we started at the mathematics and moved up in order, and only reached LLMs once the ground under them was solid.",
    modules: [
      { name: "Python Beyond Scripts (Vectorised NumPy, Pandas)", tag: "Code" },
      { name: "Linear Algebra & Calculus for ML", tag: "Math" },
      { name: "Statistics, Probability & Inference", tag: "Math" },
      { name: "Data Cleaning & Feature Engineering", tag: "Data" },
      { name: "Classical Machine Learning", tag: "ML" },
      { name: "Clustering & Dimensionality Reduction", tag: "ML" },
      { name: "Model Evaluation, Validation & Error Analysis", tag: "ML" },
      { name: "Neural Networks & Backpropagation from Scratch", tag: "DL" },
      { name: "Deep Learning Foundations", tag: "DL" },
      { name: "Sequence Models & NLP", tag: "DL" },
      { name: "Only then: LLMs, Embeddings & RAG", tag: "AI" },
      { name: "Data Structures & Algorithms", tag: "Code" },
      { name: "Portfolio Project & Interview Prep", tag: "Career" },
    ],
  },
  {
    id: "engineer",
    label: "The software engineer",
    from: "Software Engineer",
    to: "Forward Deployed Engineer",
    subtitle: "Could already ship production code. The AI half was the gap.",
    description:
      "No time spent on things they already knew. We went straight at the gaps, then spent most of the plan on systems that survive real traffic.",
    modules: [
      { name: "Math & Stats Refresher", tag: "Math" },
      { name: "ML & Deep Learning Essentials", tag: "ML" },
      { name: "Transformers & Attention", tag: "DL" },
      { name: "Embeddings & Vector Search", tag: "Infra" },
      { name: "Advanced RAG Architecture", tag: "RAG" },
      { name: "Multi-Agent Systems & Tool Use", tag: "Agents" },
      { name: "Fine-tuning & LoRA", tag: "AI" },
      { name: "Evaluation & Benchmarking", tag: "Evals" },
      { name: "AI System Design (HLD & LLD)", tag: "Design" },
      { name: "Production Deployment on AWS", tag: "Cloud" },
      { name: "Latency, Caching & Guardrails", tag: "Ops" },
      { name: "Observability & Cost Control", tag: "Ops" },
    ],
  },
  {
    id: "founder",
    label: "The founder",
    from: "SDE / Data Scientist",
    to: "Founder",
    subtitle: "Building a product, not chasing a job. Needed it to actually work.",
    description:
      "Weighted towards architecture, evaluation and cost, then well past the build. A product nobody finds is not a product, so the back half of this plan was distribution: positioning, paid ads on Meta and YouTube, and the AI tooling to produce the creative without hiring an agency.",
    modules: [
      { name: "Practical LLM Foundations", tag: "AI" },
      { name: "RAG Systems & Retrieval Quality", tag: "RAG" },
      { name: "Agents, Tools & Harness Engineering", tag: "Agents" },
      { name: "Prompt & Context Engineering", tag: "AI" },
      { name: "Hallucination Guardrails & Safety", tag: "Evals" },
      { name: "Evaluation Harnesses", tag: "Evals" },
      { name: "AI System Design (HLD & LLD)", tag: "Design" },
      { name: "Deployment, Scaling & Autoscaling", tag: "Cloud" },
      { name: "Inference Cost & Token Economics", tag: "Ops" },
      { name: "Monitoring & Incident Response", tag: "Ops" },
      { name: "Positioning, Pricing & Go-to-Market", tag: "GTM" },
      { name: "Meta & YouTube Ads, Campaign Setup", tag: "Ads" },
      { name: "AI Tools for Ad Creative & Reels", tag: "Ads" },
      { name: "Distribution, Funnels & Retention", tag: "GTM" },
    ],
  },
  {
    id: "manager",
    label: "The engineering manager",
    from: "Engineering Manager",
    to: "Director of AI",
    subtitle: "Needed to lead AI work, fund it and answer for it — not to write the kernels.",
    description:
      "A different plan on purpose. Enough hands-on to have real judgement and not be sold to, then most of the time on the things that actually sink AI programmes: money, people, law and scope. They still built a small RAG service themselves, because you cannot budget what you have never touched.",
    modules: [
      { name: "How LLMs Work — Capabilities & Limits", tag: "Literacy" },
      { name: "RAG & Agents at an Architectural Level", tag: "Literacy" },
      { name: "Build a Small RAG Service Yourself, Once", tag: "Hands-on" },
      { name: "Evaluation: Proving a System Actually Works", tag: "Literacy" },
      { name: "Why AI Projects Fail Differently to Software", tag: "Delivery" },
      { name: "Scoping, Estimation & Timelines Under Uncertainty", tag: "Delivery" },
      { name: "Budgeting AI: GPU, Token & Total Cost of Ownership", tag: "Money" },
      { name: "Build vs Buy vs Fine-tune vs Partner", tag: "Money" },
      { name: "Hiring & Structuring an AI Team", tag: "People" },
      { name: "Which Role Is Which (ML, Data, AI, MLOps)", tag: "People" },
      { name: "Interviewing for Skills You Don't Have", tag: "People" },
      { name: "Model & Data Licensing, Read Properly", tag: "Legal" },
      { name: "AI Regulation & Governance (EU AI Act, DPDP)", tag: "Legal" },
      { name: "Data Privacy, PII & Residency", tag: "Legal" },
      { name: "AI Security: Prompt Injection & Data Exfiltration", tag: "Risk" },
      { name: "Responsible AI, Bias & Auditability", tag: "Risk" },
      { name: "Measuring ROI & Reporting to Executives", tag: "Delivery" },
    ],
  },
  {
    id: "niche",
    label: "The specialist",
    from: "Data Scientist",
    to: "Research Scientist",
    subtitle: "Heading for a research lab. Wanted the layer underneath the frameworks.",
    description:
      "The narrowest plan of the five. Almost no breadth, all depth, on the parts most courses never reach.",
    modules: [
      { name: "Deep Learning Internals", tag: "DL" },
      { name: "Transformer Architecture, End to End", tag: "DL" },
      { name: "Tokenisation & Embeddings", tag: "AI" },
      { name: "Training Dynamics & Optimisers", tag: "DL" },
      { name: "Attention Variants & KV Caching", tag: "AI" },
      { name: "CUDA & Triton Kernel Programming", tag: "Kernels" },
      { name: "GPU Memory & Profiling", tag: "Kernels" },
      { name: "Quantisation & Distillation", tag: "Perf" },
      { name: "Inference Optimisation & Serving", tag: "Perf" },
      { name: "Distributed Training", tag: "Perf" },
      { name: "Reading & Reproducing Papers", tag: "Research" },
    ],
  },
];

const FULL_CATALOGUE_CATEGORIES = [
  {
    title: "1. Mathematical & Programming Foundations",
    badge: "First Principles",
    description: "The mathematical backbone that demystifies every modern AI algorithm.",
    items: [
      "Linear Algebra (Vectors, Matrices, Eigenvalues)",
      "Multivariate Calculus & Gradient Descent",
      "Probability Theory & Probability Distributions",
      "Hypothesis Testing & Statistical Inference",
      "Python for AI (Vectorized NumPy, Pandas, Data Wrangling)",
      "Relational Databases & SQL for Data Science",
      "Data Structures & Algorithms",
    ],
  },
  {
    title: "2. Classical ML & Deep Learning",
    badge: "Core AI",
    description: "Mastering supervised, unsupervised, and deep representation learning.",
    items: [
      "Regression, Classification & Tree Models",
      "Clustering & Dimensionality Reduction (k-Means, PCA, t-SNE)",
      "Ensemble Methods (Random Forests, XGBoost, LightGBM)",
      "Model Evaluation, Cross-Validation & Error Analysis",
      "Neural Networks & Backpropagation from Scratch",
      "Computer Vision & Convolutional Networks (CNNs)",
      "Sequence Models & Natural Language Processing (NLP)",
    ],
  },
  {
    title: "3. Reinforcement Learning",
    badge: "Decision Making",
    description: "Training agents that learn from consequences rather than labelled answers.",
    items: [
      "Markov Decision Processes & the Bellman Equations",
      "Value & Policy Iteration, Dynamic Programming",
      "Q-Learning & Deep Q-Networks (DQN)",
      "Policy Gradients (REINFORCE, Actor-Critic, A2C)",
      "Proximal Policy Optimization (PPO) & TRPO",
      "Multi-Armed Bandits & Exploration Strategies",
      "RLHF & DPO for Language Model Alignment",
    ],
  },
  {
    title: "4. Modern AI Engineering & GenAI",
    badge: "Production AI",
    description: "Building resilient, production-ready applications with large language models.",
    items: [
      "Generative AI & Modern LLM Architecture",
      "Prompt & Context Engineering",
      "Vector Databases & Semantic Embeddings",
      "Advanced RAG (Hybrid Search, Re-ranking, Context Compression)",
      "Autonomous Agents, Tool Calling & Function Execution",
      "Harness Engineering (Agent Loops, Scaffolding & Orchestration)",
      "Fine-Tuning Methodologies (Full FT, LoRA, QLoRA)",
      "LLM Guardrails, Hallucination Mitigation & Output Validation",
      "Systematic Model Evaluation (Ragas, TruLens, Custom Evals)",
    ],
  },
  {
    title: "5. Niche Systems, Kernels & Internals",
    badge: "High Performance",
    description: "Deep-tech systems engineering for maximal throughput and latency efficiency.",
    items: [
      "Transformer Mechanics (Self-Attention, KV-Cache, Positional Embeddings)",
      "GPU Compute Architecture & CUDA Fundamentals",
      "Kernel Programming with Triton & Custom CUDA Kernels",
      "Inference Optimization & Model Quantization (AWQ, GPTQ, GGUF)",
      "High-Scale Model Serving (vLLM, TensorRT-LLM, Continuous Batching)",
      "Distributed & Multi-GPU Training",
    ],
  },
  {
    title: "6. Production Architecture & System Design",
    badge: "Enterprise Ready",
    description: "End-to-end cloud engineering, system design rounds, and real deployment.",
    items: [
      "High-Level Design (HLD) for AI Systems & Scalable Microservices",
      "Low-Level Design (LLD), Object Modeling & Design Patterns",
      "Streaming Data Pipelines & Event-Driven AI Workflows",
      "MLOps, Model Registry & Automated CI/CD Pipelines",
      "Cloud Infrastructure Provisioning on AWS (EC2, S3, SageMaker, Lambda)",
      "Production Observability, Latency Tracing & Cost Management",
    ],
  },
];

export const Curriculum = () => {
  const [selectedTrack, setSelectedTrack] = useState(TRACKS[0].id);
  const reduced = useReducedMotion();
  const currentTrack = TRACKS.find((t) => t.id === selectedTrack) || TRACKS[0];

  return (
    <Section
      id="what-we-cover"
      align="center"
      eyebrow="Sample roadmaps"
      title="Five people. Five completely different plans."
      lead="These are examples, not a curriculum — nobody follows a fixed path here. Each one is the plan we actually designed for one person, based on where they started and what they were aiming at. Yours will look like none of them."
      maxWidth="lg"
    >
      <motion.div variants={stagger(0.08)} {...revealProps(reduced)}>
        {/* Track Explorer Buttons */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: 1.5,
            mb: { xs: 4, md: 5 },
          }}
        >
          {TRACKS.map((track) => {
            const isActive = track.id === selectedTrack;
            return (
              <Button
                key={track.id}
                onClick={() => setSelectedTrack(track.id)}
                disableElevation
                sx={{
                  px: 2.5,
                  py: 1.2,
                  borderRadius: "999px",
                  fontSize: { xs: 13.5, sm: 14.5 },
                  fontWeight: 600,
                  textTransform: "none",
                  backgroundColor: isActive ? palette.primary : palette.bgAlt,
                  color: isActive ? "#ffffff" : palette.textMuted,
                  border: `1px solid ${isActive ? palette.primary : "transparent"}`,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: isActive ? palette.primaryHover : "rgba(59, 130, 246, 0.08)",
                    color: isActive ? "#ffffff" : palette.primaryHover,
                  },
                }}
              >
                {track.label}
              </Button>
            );
          })}
        </Box>

        {/* Selected Track Pathway Card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentTrack.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: [0.22, 0.61, 0.36, 1] }}
          >
            <Box
              sx={{
                p: { xs: 3, sm: 4, md: 5 },
                borderRadius: "24px",
                backgroundColor: palette.paper,
                boxShadow: palette.shadowMd,
                border: `1px solid rgba(22, 24, 29, 0.08)`,
                textAlign: "left",
                mb: { xs: 6, md: 9 },
              }}
            >
              <Grid container spacing={{ xs: 3, md: 5 }} alignItems="center">
                <Grid item xs={12} md={5}>
                  <Box sx={{ mb: 3, maxWidth: 320 }}>
                    <PersonaScene kind={currentTrack.id} />
                  </Box>
                  <Box
                    sx={{
                      display: "inline-block",
                      px: 1.5,
                      py: 0.5,
                      borderRadius: "999px",
                      backgroundColor: palette.primarySoft,
                      color: palette.primaryHover,
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: "0.03em",
                      textTransform: "uppercase",
                      mb: 1.5,
                    }}
                  >
                    Custom Milestone Pathway
                  </Box>
                  <Typography
                    component="h3"
                    sx={{
                      color: palette.text,
                      fontWeight: 700,
                      fontSize: { xs: 22, sm: 26 },
                      letterSpacing: "-0.025em",
                      lineHeight: 1.2,
                      mb: 1,
                    }}
                  >
                    {currentTrack.label}
                  </Typography>

                  {/* The move this plan was built to make. */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: 13.5,
                        fontWeight: 600,
                        color: palette.textMuted,
                        backgroundColor: palette.bgAlt,
                        borderRadius: "999px",
                        px: 1.5,
                        py: 0.5,
                      }}
                    >
                      {currentTrack.from}
                    </Box>
                    <Box
                      aria-hidden="true"
                      component="span"
                      sx={{ color: palette.primary, fontWeight: 700, fontSize: 15 }}
                    >
                      &rarr;
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        fontSize: 13.5,
                        fontWeight: 700,
                        color: palette.primaryHover,
                        backgroundColor: palette.primarySoft,
                        borderRadius: "999px",
                        px: 1.5,
                        py: 0.5,
                      }}
                    >
                      {currentTrack.to}
                    </Box>
                  </Box>
                  <Typography
                    component="p"
                    sx={{
                      color: palette.primary,
                      fontSize: 14.5,
                      fontWeight: 600,
                      mb: 2,
                    }}
                  >
                    {currentTrack.subtitle}
                  </Typography>
                  <Typography
                    component="p"
                    sx={{
                      color: palette.textMuted,
                      fontSize: 15,
                      lineHeight: 1.68,
                    }}
                  >
                    {currentTrack.description}
                  </Typography>

                  <Box
                    sx={{
                      mt: 3,
                      pt: 2.5,
                      borderTop: `1px solid rgba(22, 24, 29, 0.06)`,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <Typography sx={{ fontSize: 13, color: palette.textMuted, fontWeight: 500 }}>
                      ⚡ <strong>1:1 Mentorship Included:</strong> Direct live coding & conceptual sessions
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: palette.textMuted, fontWeight: 500 }}>
                      ☁️ <strong>Compute Provided:</strong> AWS cloud credits & Google Colab Pro subscriptions
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={7}>
                  <Typography
                    component="h4"
                    sx={{
                      color: palette.text,
                      fontWeight: 700,
                      fontSize: 14,
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                      mb: 2,
                    }}
                  >
                    Curated Milestone Modules
                  </Typography>
                  <Box
                    component="ul"
                    sx={{
                      listStyle: "none",
                      p: 0,
                      m: 0,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                    }}
                  >
                    {currentTrack.modules.map((m, index) => (
                      <Box
                        component="li"
                        key={m.name}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          p: 1.75,
                          borderRadius: "14px",
                          backgroundColor: palette.bgAlt,
                          border: `1px solid rgba(22, 24, 29, 0.04)`,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: palette.primary,
                              width: 22,
                            }}
                          >
                            0{index + 1}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: { xs: 14, sm: 15 },
                              fontWeight: 600,
                              color: palette.text,
                            }}
                          >
                            {m.name}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            fontSize: 11.5,
                            fontWeight: 700,
                            px: 1.2,
                            py: 0.35,
                            borderRadius: "999px",
                            backgroundColor: palette.primarySoft,
                            color: palette.primaryHover,
                            textTransform: "uppercase",
                          }}
                        >
                          {m.tag}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </motion.div>
        </AnimatePresence>

        {/* Full Comprehensive Catalogue Grid */}
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 5 } }}>
          <Typography
            component="h3"
            sx={{
              color: palette.text,
              fontWeight: 700,
              fontSize: { xs: 24, sm: 30 },
              letterSpacing: "-0.025em",
              mb: 1.5,
            }}
          >
            The Full Curriculum Range
          </Typography>
          <Typography
            component="p"
            sx={{
              color: palette.textMuted,
              fontSize: 16,
              maxWidth: 640,
              mx: "auto",
            }}
          >
            Everything below is taught in-house. Your milestone plan selects what you need, leaving out what you don't.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {FULL_CATALOGUE_CATEGORIES.map((cat) => (
            <Grid item xs={12} md={6} lg={4} key={cat.title}>
              <motion.div variants={fadeUp} style={{ height: "100%" }}>
                <Box
                  sx={{
                    p: 3.5,
                    height: "100%",
                    borderRadius: "20px",
                    backgroundColor: palette.paper,
                    border: `1px solid rgba(22, 24, 29, 0.08)`,
                    boxShadow: palette.shadowSm,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    textAlign: "left",
                  }}
                >
                  <Box>
                    <Box
                      sx={{
                        display: "inline-block",
                        px: 1.25,
                        py: 0.4,
                        borderRadius: "999px",
                        backgroundColor: palette.bgAlt,
                        color: palette.textMuted,
                        fontSize: 11.5,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.03em",
                        mb: 1.5,
                      }}
                    >
                      {cat.badge}
                    </Box>
                    <Typography
                      component="h4"
                      sx={{
                        color: palette.text,
                        fontWeight: 700,
                        fontSize: 18,
                        lineHeight: 1.3,
                        mb: 1,
                      }}
                    >
                      {cat.title}
                    </Typography>
                    <Typography
                      component="p"
                      sx={{
                        color: palette.textMuted,
                        fontSize: 13.5,
                        lineHeight: 1.55,
                        mb: 2.5,
                      }}
                    >
                      {cat.description}
                    </Typography>

                    <Box
                      component="ul"
                      sx={{
                        listStyle: "none",
                        p: 0,
                        m: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      {cat.items.map((item) => (
                        <Box
                          component="li"
                          key={item}
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1.25,
                            fontSize: 13.5,
                            color: palette.text,
                            lineHeight: 1.5,
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              width: 5,
                              height: 5,
                              borderRadius: "50%",
                              backgroundColor: palette.primary,
                              mt: "8px",
                              flexShrink: 0,
                            }}
                          />
                          <span>{item}</span>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </motion.div>
    </Section>
  );
};

export default Curriculum;
